import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Bot, Loader2, Sparkles, GraduationCap } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

const markdownComponents = {
  h1: ({ children }) => <h3 className="text-lg font-bold mt-5 mb-2 text-foreground">{children}</h3>,
  h2: ({ children }) => <h3 className="text-base font-bold mt-5 mb-2 text-foreground">{children}</h3>,
  h3: ({ children }) => <h4 className="text-sm font-bold mt-4 mb-1.5 text-foreground">{children}</h4>,
  p: ({ children }) => <p className="mb-3 leading-7">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  ul: ({ children }) => <ul className="mb-3 pl-5 space-y-1.5 list-disc">{children}</ul>,
  ol: ({ children }) => <ol className="mb-3 pl-5 space-y-1.5 list-decimal">{children}</ol>,
  li: ({ children }) => <li className="leading-7">{children}</li>,
  hr: () => <hr className="my-4 border-border" />,
  blockquote: ({ children }) => <blockquote className="border-l-3 border-primary/30 pl-4 my-3 text-muted-foreground italic">{children}</blockquote>,
};

export default function SharedSolution() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [solution, setSolution] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchSolution = async () => {
      try {
        const res = await axios.get(`${API}/ai-chat/shared/${shareId}`);
        setSolution(res.data);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchSolution();
  }, [shareId]);

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col" data-testid="shared-solution-page">
      {/* Brand header */}
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="shared-home-btn">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold font-['Manrope'] text-foreground text-sm sm:text-base">TheMatheMind 360</span>
          </button>
          <Button size="sm" className="rounded-full h-8 text-xs" onClick={() => navigate("/login?redirect=/ai-tutor")} data-testid="shared-try-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />Try MathBot
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-6 md:py-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mb-3" />
            <p className="text-sm">Loading solution…</p>
          </div>
        ) : error ? (
          <div className="text-center py-24" data-testid="shared-not-found">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Bot className="w-7 h-7 text-muted-foreground" />
            </div>
            <h1 className="text-lg font-bold text-foreground mb-2">Solution not found</h1>
            <p className="text-sm text-muted-foreground mb-6">This link may have expired or is incorrect.</p>
            <Button className="rounded-full" onClick={() => navigate("/login?redirect=/ai-tutor")}>Ask MathBot your own question</Button>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-sm font-semibold text-foreground font-['Manrope'] leading-tight">MathBot Solution</h1>
                <p className="text-[11px] text-muted-foreground leading-tight">Shared by {solution.author_name}</p>
              </div>
            </div>

            {solution.user_message && (
              <div className="mb-4 rounded-xl border border-border bg-muted/30 px-4 py-3" data-testid="shared-question">
                <span className="text-xs font-semibold text-foreground">Question:</span>{" "}
                <span className="text-sm text-muted-foreground">{solution.user_message}</span>
              </div>
            )}

            <div
              className="ai-response-content text-sm text-foreground leading-relaxed break-words bg-white p-5 rounded-xl border border-border shadow-sm [&_.katex-display]:overflow-x-auto [&_.katex]:text-[0.9em]"
              data-testid="shared-answer"
            >
              <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]} components={markdownComponents}>
                {solution.ai_response}
              </ReactMarkdown>
            </div>

            <div className="mt-8 rounded-2xl bg-primary/5 border border-primary/15 p-6 text-center">
              <h2 className="text-base font-bold text-foreground mb-1.5">Stuck on your own maths problem?</h2>
              <p className="text-sm text-muted-foreground mb-4">MathBot solves it step-by-step with mental-maths and Vedic tricks.</p>
              <Button className="rounded-full" onClick={() => navigate("/login?redirect=/ai-tutor")} data-testid="shared-cta-btn">
                <Sparkles className="w-4 h-4 mr-2" />Solve it with MathBot
              </Button>
            </div>

            <p className="text-[11px] text-muted-foreground text-center mt-6">MathBot can make mistakes. Always verify important calculations.</p>
          </div>
        )}
      </main>
    </div>
  );
}
